import React, {useContext, useRef, useState} from 'react';
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';

import {withStyles} from "@material-ui/core/styles";
import {Button, IconButton} from "@material-ui/core";
import {AddBoxOutlined, DeleteOutline, PageviewOutlined} from "@material-ui/icons";
import DataGrid from "../../../controls/DataGrid";
import {DropzoneFileUpload} from "../../../controls";
import {DocumentType} from "../../../icons";
import DocumentViewer from "../../Documents/DocumentViewer";
import SystemContext from "../../../SystemContext";

const styles = {
  header: {
    "&.ag-header-cell": {
      paddingLeft: 3, paddingRight: 3, fontSize: 12
    }
  },
  column: {
    "&.ag-cell-not-inline-editing": {
      paddingLeft:4, paddingRight: 4
    }
  },
  badge: {
    height: "24px",
    width: "48px",
    display: "table-cell",
    textAlign: "center",
    verticalAlign: "middle",
    borderRadius: "25%",
    background: "lightGray"
  }
};

const BookingDocumentsGrid = (_props) => {
  const props = {
    ..._props
  }
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [state, setState] = useState({
    canEdit: props.canEdit||false,
    activeRow: null,
    showDocumentViewer: false,
  });
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: "",
  });
  if (props.canEdit && !state.canEdit) {
    setState({...state, canEdit: props.canEdit});
    if (gridApi)
      gridApi.redrawRows();
  }

  const SystemData = useContext(SystemContext);

  const dataGridRef = useRef();
  const documentTypeRef = useRef();
  const documents = props.documents;
  const awbNumber = props.awbNumber;


  // ==================
  // Grid setup
  // ==================
  const { classes } = props;
  const onGridReady = (params) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
    params.api.sizeColumnsToFit();
    if (props.onGridReady) {
      props.onGridReady(params);
    }
  };

  const onRowSelected = (params) => {
    if (params.type === "rowSelected") {
      setSelectedRow({
        rowIndex: params.rowIndex,
        rowData: params.data
      })
    }
    else {
      setSelectedRow(null);
    }
  };

  function setAutoHeight() {
    if (gridApi.getDisplayedRowCount() >= 3) {
      gridApi.setDomLayout('autoHeight');
      // auto height will get the grid to fill the height of the contents,
      // so the grid div should have no height set, the height is dynamic.
      document.querySelector('#documentsGrid').style.height = '';
    }
  }

  const onRowDataUpdated = (params) => {
    saveDocuments(params.api);
  }

  // ==================
  // Grid functions
  // ==================
  const getEmptyRow = () => {
    const columns = getColumns({canEdit: state.canEdit});
    const emptyRow = {};
    if (columns && Array.isArray(columns)) {
      columns.map(column => {
        emptyRow[column.field] = "";
      })
    }
    emptyRow["id"] = uuidv4();
    emptyRow["documentType"] = "OTHER";
    return (emptyRow);
  };

  const cellValueChanged = (params) => {
    if (params.colDef.field === "documentType" && params.data.fileName === "") {
      params.api.redrawRows({rowNodes: [params.node]});
    }
    saveDocuments(params.api);
  }

  const saveDocuments = (api) => {
    if (props.onChange) {
      let documents = [];
      const model = api.getModel();
      const gridRows = model.rowsToDisplay;
      gridRows.forEach( row => {
        documents.push(row.data)
      });
      props.onChange(documents);
    }
  }

  const handleRemoveItem = (data, api, node) => {
    if (!node)  return;
    api.applyTransaction({ remove: [data]});

    // SystemData.confirm(
    //   `Delete cargo particulars`,
    //   `Are you sure you wish to remove the ${itemToRemove}: '${params.contract[params.contract.length - 1]}'`,
    //   [
    //     {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
    //     {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
    //   ],
    //   (response) => {
    //     if (response) {
    //
    //     }
    //   }
    // );
  }

  const renderActionButtons = (params) => {
    return (
      <>
        {
          <IconButton
            color="secondary"
            onClick={() => { handleRemoveItem(params.data, params.api, params.node) }}
            disabled={params.disabled}
            size={"small"}
          >
            <DeleteOutline/>
          </IconButton>
        }
      </>
    )
  }

  const renderFileName = (params) => {
    if (params && params.value)
    {
      return params.value;
    }
    return (
      <DropzoneFileUpload
        awbNumber={awbNumber}
        documentUuid={params.data.id}
        accept=".doc, .docx, .xls, .xlsx, .jpg, .png, .pdf, .zip"
        dropzoneStyle={{ height: 38,  maxHeight: 38, minHeight: 38, border: "1px dashed #929292" }}
        inputLabel={{fontSize: 17, color: "#fd5b02"}}
        inputContent={params.data.documentType === "" ? "Select a 'Document Type' to enable upload" :  "Drop a File or Click to browse"}
        disabled={!params.canEdit || params.data.documentType === ""}
        onChange={(result)=>{
          if (result && result.status) {
            let node;
            if (params.api) {
              node = params.api.getRowNode(result.id);
              if (node) {
                const fileType = result.fileName.split('.');
                const fileSize = result.fileSize > 1000000
                  ? `${(result.fileSize / 1000000).toFixed(0)}MB`
                  : result.fileSize > 1000
                    ? `${(result.fileSize / 1000).toFixed(0)}KB`
                    : `${result.fileSize}B`
                const newData = {
                  ...node.data,
                  fileName: result.fileName,
                  fileType: Array.isArray(fileType) && fileType.length > 1 ? fileType[fileType.length - 1] : "",
                  fileSize: fileSize,
                  fileUrl: result.url
                }
                node.setData(newData);
                saveDocuments(params.api);
              }
            }
          }
        }}
      />
    )
  }

  const renderFileType = (params) => {

    let fileType = params.data.fileType;
    let contentType = "application/octet-stream";
    switch (fileType?.toLowerCase()) {
      case "doc":
        contentType = "application/msword";
        break;
      case "docx":
        contentType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        break;
      case "xls":
        contentType = "application/ms-excel";
        break;
      case "xlsx":
        contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        break;
      case "jpg":
        contentType = "image/jpeg";
        break;
      case "pdf":
        contentType = "application/pdf";
        break;
      case "png":
        contentType = "image/png";
        break;
      default:
        contentType = "application/octet-stream";
        break;
    }
    let name = params.data.fileName;
    let url = params.data.fileUrl;

    const handleDragStart = (event) => {
      event.dataTransfer.setData("DownloadURL", `${contentType}:${name}:${url}`);
      event.dataTransfer.setData('text/html', `<a href="${url}">${name}</a>`);
    }

    return (
      <>
        {
          <div
            id={params.data.id}
            draggable="true"
            onDragStart={handleDragStart}
          >
            <IconButton
              onClick={()=>{
                if (params.data && __.has(params.data, "fileUrl") && params.data.fileUrl !== "") {
                  const documentsToView = [
                    {
                      documentType: params.data.documentType,
                      documentDescription: params.data.fileDescription,
                      fileName: params.data.fileName,
                      fileType: params.data.fileType,
                      uri: params.data.fileUrl
                    }
                  ];
                  setState({...state, showDocumentViewer: true, documentsToView})
                }
              }}
            >
              <DocumentType
                style={{width:32, height:32, marginTop: -6}}
                filetype={params.node.data.fileType}
              />
            </IconButton>
          </div>
        }
      </>
    )
  }

  const renderFileSize = (params) => {
    return ( <div className={classes.badge}> {params.node.data.fileSize} </div> )
  }

  // ==================
  // Value Getters
  // ==================
  const hashValueGetter = function (params) {
    return params.node.rowIndex + 1;
  };


  // ==================
  // The grid structure
  // ==================
  const getColumns = (props) => {
    const baseColumn = {
      options: {
        cellStyle: { fontSize: 7, },
        cellClass: classes.column,
        headerClass: classes.header,
      },
      editable: state.canEdit,
      headerClass: 'classes'
    };

    return [
      {
        headerName: "",
        ...baseColumn,
        field: "id",
        editable: false,
        width: 0,
        valueFormatter: () => ("")
      },
      {
        headerName: "#",
        ...baseColumn,
        field: "sequence",
        valueGetter: (params) => {
          const sequence = hashValueGetter(params);
          params.node.data.sequence = sequence;
          return sequence;
        },
        maxWidth: 50,
        editable: false,
      },
      {
        headerName: "Document Type",
        ...baseColumn,
        field: "documentType",
        width: 150,
        cellEditor: {
          component: "ComboSelect",
          params: {
            id: "documentType",
            ref: {documentTypeRef},
            value: props.value,
            group: "DOCUMENT TYPES",
            subGroup: "SHIPMENT",
            style: {width:"100%", fontSize: "0.875rem", textAlign: "left"},
          }
        },
        cellRenderer: {
          component: "ComboSelect",
          params: {
            id: "documentType",
            ref: {documentTypeRef},
            value: props.value,
            group: "DOCUMENT TYPES",
            subGroup: "SHIPMENT",
            displayOnly: true,
            style: {width: "100%", fontSize: "0.875rem", textAlign: "left"},
          },
        },
      },
      {
        headerName: "Description",
        ...baseColumn,
        field: "fileDescription",
        width: 150,
      },
      {
        headerName: " ",
        ...baseColumn,
        field: "fileType",
        editable: false,
        navigable: false,
        cellRenderer: {
          component: "renderFileType",
        },
        width: 65,
      },
      {
        headerName: " ",
        ...baseColumn,
        field: "fileSize",
        editable: false,
        navigable: false,
        // cellRenderer: {
        //   component: "renderFileSize",
        // },
        width: 70,
      },
      {
        headerName: "File",
        ...baseColumn,
        field: "fileName",
        editable: false,
        flex: 1,
        cellRenderer: {
          component: "renderFileName",
          params: {
            id: "fileName",
            value: props.value,
            canEdit: state.canEdit
          }
        },
        valueFormatter: (params) => { return params.node.data.fileName}
      },
      {
        ...baseColumn,
        field: 'deleteAction',
        headerName: ' ',
        editable: false,
        cellRenderer: {
          component: "renderActionButtons",
          params: {
            disabled: !props.canEdit,
          }
        },
        maxWidth: 40,
        cellStyle: { border: "none" },
      },
    ];
  };

  const dataGridComponents = {renderActionButtons, renderFileType, renderFileName, renderFileSize};

  return (
    // <div style={{width: props.width?props.width:"100%", ...props.style}} className="ag-theme-material">
    <div style={{width: props.width?props.width:"100%", ...props.style, overflow: "scroll", maxHeight: 700 }} >

      <Button
        style={{height:24, marginTop:4}}
        variant="text"
        color="primary"
        disabled={!props.canEdit}
        startIcon={<AddBoxOutlined/>}
        //className={classes.button}
        onClick={()=>{
          setAutoHeight();
          //gridApi.applyTransaction({add: [getEmptyRow()], addIndex:0 })
          gridApi.applyTransaction({add: [getEmptyRow()] })
          saveDocuments(gridApi);
        }}
      >
        {`Add Document`}
      </Button>
      <Button
        style={{height:24, marginTop:4}}
        variant="text"
        color="primary"
        disabled={gridApi && gridApi.getModel().rowsToDisplay.length === 0}
        startIcon={<PageviewOutlined/>}
        //className={classes.button}
        onClick={()=>{
          let documentsToView = [];
          if(gridApi){
            const model = gridApi.getModel();
            const gridRows = model.rowsToDisplay;
            gridRows.forEach( row => {
              if (__.has(row.data, "fileUrl") && row.data.fileUrl !== "") {
                const document = {
                  documentType: row.data.documentType,
                  documentDescription: row.data.fileDescription,
                  fileName: row.data.fileName,
                  fileType: row.data.fileType,
                  uri: row.data.fileUrl
                }
                documentsToView.push(document);
              }
            });
          }
          if (documentsToView.length < 1) {
            SystemData.alert("", `There are no documents with valid files attached to view`);
            return;
          }
          setState({...state, showDocumentViewer: true, documentsToView})
        }}
      >
        {`View Documents`}
      </Button>

      <DataGrid
        id="documentsGrid"
        title="Documents"
        ref={dataGridRef}
        components={dataGridComponents}
        columns={getColumns({canEdit: state.canEdit})}
        rows={documents}
        height={props.height||`calc(100vh - ${(290)}px)`}
        onGridReady={onGridReady}
        onRowSelected={onRowSelected}
        onRowDataUpdated={onRowDataUpdated}
        //groupIncludeFooter={true}
        //groupIncludeTotalFooter={true}

        options={{
          getRowNodeId: data => data.id,
          singleClickEdit: true,
          suppressCellSelection: true,
          //editType:'fullRow',
          onCellValueChanged: (params)=>{cellValueChanged(params)}, //prevent default onCellValueChanged
          defaultColDef: {
            editable: true,
            resizable: false,
            menuTabs: []
          },
          overlayLoadingTemplate:
            '<span class="ag-overlay-loading-center">Please wait while your data are loading</span>',
          overlayNoRowsTemplate:
            '<span style="padding: 10px; border: 1px solid #111; background: #fdecea;"> Click   \'Add Document\' to load a shipment document</span>',
        }}
      />
      {state.showDocumentViewer
        ?
        <DocumentViewer
          dialogStates=
            {
              {showDialog: state.showDocumentViewer,
                title: `Documents Viewer for ${awbNumber}`,
                record: state.documentsToView[0]
              }
            }
          close={()=>{setState({...state, showDocumentViewer: false})}}
          documents={state.documentsToView}
        />
        :
        <></>
      }
    </div>
  );

}

export default withStyles(styles)(BookingDocumentsGrid);
